<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import ElevenlabsAPI from 'dashboard/api/aiAgents/elevenlabs';
import DeploysAPI from 'dashboard/api/aiAgents/deploys';
import AgentLayout from 'dashboard/components-next/aiAgents/agent/AgentLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useBrain } from './useBrain';

// Voice tab (Part 2 B): edits the brain's `el` block (the ElevenLabs render target) on its own tab,
// folding it back through the same draft-spread save path the other tabs use. The account EL key is
// a write-only account connector (shared across agents). "Deploy to ElevenLabs" creates the
// elevenlabs deploy intent; the runtime renders + pushes and reports sync_state/el_agent_id back.

const EL_FIELDS = ['agent_name', 'first_message', 'language', 'response_timeout_secs', 'voice_id', 'voice_system_prompt'];

const { t } = useI18n();
const store = useStore();
const { brain, fetchBrain } = useBrain();

const uiFlags = useMapGetter('aiAgentBrains/getUIFlags');
const isSaving = computed(() => uiFlags.value.updatingItem);

const el = reactive({});
const hasConflict = ref(false);
const isHydrated = ref(false);

const elConfigured = ref(false);
const apiKeyInput = ref('');
const isSavingKey = ref(false);
const isRemovingKey = ref(false);
const voices = ref([]);
const isDeploying = ref(false);
const deployBlockers = ref([]);

const elDeploy = computed(
  () => (brain.value.deploys || []).find(deploy => deploy.target === 'elevenlabs') || null
);
const hasPublishedVersion = computed(() => !!brain.value.latest_version_id);

const hydrateFromDraft = () => {
  const draftEl = brain.value.draft_config?.el || {};
  Object.keys(el).forEach(key => delete el[key]);
  EL_FIELDS.forEach(key => {
    el[key] = draftEl[key] ?? (key === 'response_timeout_secs' ? 20 : '');
  });
  isHydrated.value = true;
};

const loadVoices = async () => {
  try {
    const { data } = await ElevenlabsAPI.voices();
    voices.value = data.voices || [];
  } catch (error) {
    voices.value = [];
  }
};

const loadElConfig = async () => {
  try {
    const { data } = await ElevenlabsAPI.getConfig();
    elConfigured.value = !!data.el_configured;
    if (elConfigured.value) await loadVoices();
  } catch (error) {
    elConfigured.value = false;
  }
};

watch(
  () => brain.value.id,
  id => {
    if (id && !isHydrated.value) {
      hydrateFromDraft();
      loadElConfig();
    }
  },
  { immediate: true }
);

const save = async () => {
  try {
    await store.dispatch('aiAgentBrains/update', {
      id: brain.value.id,
      draftConfig: {
        ...(brain.value.draft_config || {}),
        el: {
          ...(brain.value.draft_config?.el || {}),
          ...el,
        },
      },
      lockVersion: brain.value.lock_version,
    });
    hasConflict.value = false;
    useAlert(t('AI_AGENTS.VOICE.SAVED'));
  } catch (error) {
    if (error.response?.status === 409) {
      hasConflict.value = true;
    } else {
      useAlert(t('AI_AGENTS.VOICE.ERROR'));
    }
  }
};

const reloadDraft = async () => {
  await fetchBrain();
  hydrateFromDraft();
  hasConflict.value = false;
};

const saveKey = async () => {
  const apiKey = apiKeyInput.value.trim();
  if (!apiKey) return;
  isSavingKey.value = true;
  try {
    const { data } = await ElevenlabsAPI.setKey(apiKey);
    elConfigured.value = !!data.el_configured;
    apiKeyInput.value = '';
    if (elConfigured.value) await loadVoices();
    useAlert(t('AI_AGENTS.VOICE.API_KEY.SAVED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.VOICE.API_KEY.ERROR'));
  } finally {
    isSavingKey.value = false;
  }
};

const removeKey = async () => {
  isRemovingKey.value = true;
  try {
    const { data } = await ElevenlabsAPI.clearKey();
    elConfigured.value = !!data.el_configured;
    voices.value = [];
    useAlert(t('AI_AGENTS.VOICE.API_KEY.REMOVED'));
  } catch (error) {
    useAlert(t('AI_AGENTS.VOICE.API_KEY.ERROR'));
  } finally {
    isRemovingKey.value = false;
  }
};

const deployToEl = async () => {
  if (!hasPublishedVersion.value) return;
  isDeploying.value = true;
  deployBlockers.value = [];
  try {
    await DeploysAPI.create({
      brainId: brain.value.id,
      deploy: { target: 'elevenlabs', version_id: brain.value.latest_version_id },
    });
    await fetchBrain();
    useAlert(t('AI_AGENTS.VOICE.DEPLOY.SUCCESS'));
  } catch (error) {
    if (error.response?.status === 422 && error.response.data?.blockers) {
      deployBlockers.value = error.response.data.blockers;
    } else {
      useAlert(t('AI_AGENTS.VOICE.DEPLOY.ERROR'));
    }
  } finally {
    isDeploying.value = false;
  }
};

const inputClass =
  'px-3 py-2 text-sm font-normal rounded-lg outline outline-1 outline-n-weak bg-n-solid-1 text-n-slate-12';
</script>

<template>
  <AgentLayout v-if="brain.id" :brain="brain">
    <div class="flex flex-col gap-4 max-w-3xl">
      <div>
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('AI_AGENTS.VOICE.HEADER') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.VOICE.DESCRIPTION') }}
        </p>
      </div>

      <div
        v-if="hasConflict"
        class="flex items-center justify-between gap-3 px-3 py-2 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
      >
        {{ t('AI_AGENTS.AGENT.CONFLICT.MESSAGE') }}
        <Button
          :label="t('AI_AGENTS.AGENT.CONFLICT.RELOAD')"
          color="amber"
          size="sm"
          @click="reloadDraft"
        />
      </div>

      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.VOICE.FIELDS.AGENT_NAME') }}
        <input v-model="el.agent_name" type="text" :class="inputClass" />
      </label>

      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.VOICE.FIELDS.FIRST_MESSAGE') }}
        <textarea
          v-model="el.first_message"
          rows="2"
          :placeholder="t('AI_AGENTS.VOICE.FIELDS.FIRST_MESSAGE_PLACEHOLDER')"
          :class="inputClass"
        />
      </label>

      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.VOICE.FIELDS.LANGUAGE') }}
          <input
            v-model="el.language"
            type="text"
            :placeholder="t('AI_AGENTS.VOICE.FIELDS.LANGUAGE_PLACEHOLDER')"
            :class="inputClass"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.VOICE.FIELDS.RESPONSE_TIMEOUT') }}
          <input
            v-model.number="el.response_timeout_secs"
            type="number"
            min="1"
            step="1"
            :class="inputClass"
          />
        </label>
      </div>

      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.VOICE.FIELDS.VOICE') }}
        <select v-if="voices.length" v-model="el.voice_id" :class="inputClass">
          <option v-for="voice in voices" :key="voice.voice_id" :value="voice.voice_id">
            {{ voice.name }}
          </option>
        </select>
        <template v-else>
          <input
            v-model="el.voice_id"
            type="text"
            :placeholder="t('AI_AGENTS.VOICE.FIELDS.VOICE_PLACEHOLDER')"
            :class="inputClass"
          />
          <span class="text-xs font-normal text-n-slate-11">
            {{ t('AI_AGENTS.VOICE.FIELDS.VOICE_FALLBACK_HINT') }}
          </span>
        </template>
      </label>

      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('AI_AGENTS.VOICE.FIELDS.VOICE_SYSTEM_PROMPT') }}
        <textarea v-model="el.voice_system_prompt" rows="3" :class="inputClass" />
      </label>

      <div class="flex justify-end">
        <Button
          :label="t('AI_AGENTS.VOICE.SAVE')"
          :is-loading="isSaving"
          :disabled="isSaving"
          @click="save"
        />
      </div>

      <div class="flex flex-col gap-3 p-3 rounded-lg outline outline-1 outline-n-weak bg-n-solid-1">
        <div class="flex items-center justify-between gap-2">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('AI_AGENTS.VOICE.API_KEY.LABEL') }}
          </h3>
          <span
            v-if="elConfigured"
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-teal-3 text-n-teal-11"
          >
            {{ t('AI_AGENTS.VOICE.API_KEY.CONFIGURED') }}
          </span>
          <span
            v-else
            class="px-2 py-0.5 text-xs font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
          >
            {{ t('AI_AGENTS.VOICE.API_KEY.NOT_CONFIGURED') }}
          </span>
        </div>

        <p class="text-xs text-n-slate-11">
          {{ t('AI_AGENTS.VOICE.API_KEY.HELP') }}
        </p>

        <div class="flex items-end gap-2">
          <label class="flex flex-col flex-1 gap-1 text-sm font-medium text-n-slate-12">
            <input
              v-model="apiKeyInput"
              type="password"
              autocomplete="off"
              :placeholder="t('AI_AGENTS.VOICE.API_KEY.PLACEHOLDER')"
              :class="inputClass"
            />
          </label>
          <Button
            :label="t('AI_AGENTS.VOICE.API_KEY.SAVE')"
            :is-loading="isSavingKey"
            :disabled="!apiKeyInput.trim() || isSavingKey"
            size="sm"
            @click="saveKey"
          />
          <Button
            v-if="elConfigured"
            :label="t('AI_AGENTS.VOICE.API_KEY.REMOVE')"
            variant="outline"
            color="ruby"
            size="sm"
            :is-loading="isRemovingKey"
            :disabled="isRemovingKey"
            @click="removeKey"
          />
        </div>
      </div>

      <div class="flex flex-col gap-3 p-3 rounded-lg outline outline-1 outline-n-weak bg-n-solid-1">
        <div class="flex items-center justify-between gap-2">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('AI_AGENTS.VOICE.DEPLOY.TITLE') }}
          </h3>
          <span
            v-if="elDeploy"
            class="px-2 py-0.5 text-xs font-medium rounded-full"
            :class="
              elDeploy.sync_state === 'synced'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            {{ t(`AI_AGENTS.VOICE.DEPLOY.${elDeploy.sync_state.toUpperCase()}`) }}
          </span>
        </div>

        <p class="text-xs text-n-slate-11">
          {{ t('AI_AGENTS.VOICE.DEPLOY.DESCRIPTION') }}
        </p>

        <p v-if="elDeploy?.el_agent_id" class="text-xs font-mono text-n-slate-11">
          {{ t('AI_AGENTS.VOICE.DEPLOY.AGENT_ID', { id: elDeploy.el_agent_id }) }}
        </p>

        <p v-if="deployBlockers.length" class="text-xs text-n-ruby-11">
          {{ t('AI_AGENTS.VOICE.DEPLOY.BLOCKED', { blockers: deployBlockers.join(', ') }) }}
        </p>

        <p v-if="!hasPublishedVersion" class="text-xs text-n-amber-11">
          {{ t('AI_AGENTS.VOICE.DEPLOY.NO_VERSION') }}
        </p>

        <div class="flex justify-start">
          <Button
            :label="t('AI_AGENTS.VOICE.DEPLOY.BUTTON')"
            :is-loading="isDeploying"
            :disabled="isDeploying || !hasPublishedVersion"
            size="sm"
            @click="deployToEl"
          />
        </div>
      </div>
    </div>
  </AgentLayout>
  <div v-else class="flex items-center justify-center w-full h-full text-n-slate-11">
    <Spinner />
  </div>
</template>
